/**
 * Consultant Discovery Console
 * Main dashboard for theme-based discovery management
 * Phase 2: Consultant Tools
 */

import React, { useState, useEffect } from 'react';
import { CheckCircle2, Circle, AlertCircle, TrendingUp, Lightbulb, ChevronRight, Settings, Loader2 } from 'lucide-react';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import { ThemeDetailView } from './ThemeDetailView';
import { QuestionTemplateManager } from './QuestionTemplateManager';

interface DiscoveryTheme {
  id: string;
  name: string;
  phase: string;
  status: 'not-started' | 'in-progress' | 'complete';
  critical?: boolean;
  completedDate?: string;
  configItems?: Record<string, ConfigItemDiscovery>;
  dataImportNeeded?: boolean;
  dataReceived?: boolean;
}

interface ConfigItemDiscovery {
  id: string;
  name: string;
  status: 'not-started' | 'discovery' | 'ready-to-build' | 'building' | 'built';
  buildReadiness?: number;
  answeredQuestions?: any[];
  missingInfo?: string[];
}

interface CrossThemeInsight {
  id: string;
  insight: string;
  affectedThemes: string[];
  recommendation: string;
  confidence: 'high' | 'medium' | 'low';
  actionable?: boolean;
}

interface DiscoveryContext {
  companyId: string;
  globalKnowledge: any;
  discoveryThemes: Record<string, DiscoveryTheme>;
  crossThemeInsights: CrossThemeInsight[];
}

interface BuildReadinessSummary {
  readyToBuild: any[];
  inProgress: any[];
  notStarted: any[];
  summary: {
    total: number;
    readyCount: number;
    inProgressCount: number;
    notStartedCount: number;
  };
}

export function ConsultantDiscoveryConsole({ 
  companyId, 
  accessToken 
}: { 
  companyId: string;
  accessToken: string;
}) {
  const [context, setContext] = useState<DiscoveryContext | null>(null);
  const [buildReadiness, setBuildReadiness] = useState<BuildReadinessSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'overview' | 'themes' | 'build-readiness' | 'insights' | 'templates'>('overview');
  const [selectedTheme, setSelectedTheme] = useState<string | null>(null);
  const [analyzingInsights, setAnalyzingInsights] = useState(false);

  // Load discovery context
  useEffect(() => {
    loadDiscoveryContext();
    loadBuildReadiness();
  }, [companyId]);

  const loadDiscoveryContext = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/discovery-context/${companyId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load discovery context');
      }

      const data = await response.json();
      setContext(data.context);
      
      // Show auto-sync notification if CIs were synced
      if (data.autoSynced && data.syncedCount > 0) {
        console.log(`[DiscoveryConsole] Auto-synced ${data.syncedCount} config items into discovery themes`);
      }
      
      setError(null);
    } catch (err: any) {
      console.error('Error loading discovery context:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadBuildReadiness = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/discovery-context/${companyId}/build-readiness`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load build readiness');
      }

      const data = await response.json();
      setBuildReadiness(data);
    } catch (err: any) {
      console.error('Error loading build readiness:', err);
    }
  };

  const runCrossThemeAnalysis = async () => {
    try {
      setAnalyzingInsights(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/discovery-context/${companyId}/analyze-cross-theme`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to analyze cross-theme insights');
      }

      const data = await response.json();
      
      // Reload context to get updated insights
      await loadDiscoveryContext();
      
      setActiveView('insights');
    } catch (err: any) {
      console.error('Error analyzing insights:', err);
      alert(`Failed to analyze insights: ${err.message}`);
    } finally {
      setAnalyzingInsights(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="size-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error || !context) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800">Failed to load discovery context: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-gray-900">Discovery Console</h2>
          <p className="text-gray-600 mt-1">Theme-based discovery management with holistic intelligence</p>
        </div>
        <button
          onClick={runCrossThemeAnalysis}
          disabled={analyzingInsights}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
        >
          {analyzingInsights ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <Lightbulb className="size-4" />
              Run AI Analysis
            </>
          )}
        </button>
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveView('overview')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeView === 'overview'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveView('themes')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeView === 'themes'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Themes
        </button>
        <button
          onClick={() => setActiveView('build-readiness')}
          className={`px-4 py-2 font-medium transition-colors relative ${
            activeView === 'build-readiness'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Build Readiness
          {buildReadiness && buildReadiness.summary.readyCount > 0 && (
            <span className="absolute -top-1 -right-1 size-5 bg-green-500 text-white text-xs rounded-full flex items-center justify-center">
              {buildReadiness.summary.readyCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveView('insights')}
          className={`px-4 py-2 font-medium transition-colors relative ${
            activeView === 'insights'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          AI Insights
          {context.crossThemeInsights.length > 0 && (
            <span className="absolute -top-1 -right-1 size-5 bg-purple-500 text-white text-xs rounded-full flex items-center justify-center">
              {context.crossThemeInsights.length}
            </span>
          )}
        </button>
      </div>

      {/* Content */}
      {activeView === 'overview' && (
        <OverviewView 
          context={context} 
          buildReadiness={buildReadiness}
          onSelectTheme={(themeId) => {
            setSelectedTheme(themeId);
            setActiveView('themes');
          }}
        />
      )}

      {activeView === 'themes' && (
        <ThemesView 
          context={context} 
          selectedTheme={selectedTheme}
          onSelectTheme={setSelectedTheme}
          companyId={companyId}
          accessToken={accessToken}
        />
      )}

      {activeView === 'build-readiness' && buildReadiness && (
        <BuildReadinessView 
          buildReadiness={buildReadiness}
          companyId={companyId}
          accessToken={accessToken}
        />
      )}

      {activeView === 'insights' && (
        <InsightsView 
          insights={context.crossThemeInsights}
          onRunAnalysis={runCrossThemeAnalysis}
          analyzing={analyzingInsights}
        />
      )}

      {activeView === 'templates' && (
        <QuestionTemplatesView 
          companyId={companyId}
          accessToken={accessToken}
        />
      )}
    </div>
  );
}

// Overview View Component
function OverviewView({ 
  context, 
  buildReadiness,
  onSelectTheme 
}: { 
  context: DiscoveryContext;
  buildReadiness: BuildReadinessSummary | null;
  onSelectTheme: (themeId: string) => void;
}) {
  const themes = Object.values(context.discoveryThemes);
  const foundationThemes = themes.filter(t => t.phase === 'Foundation');
  const implementationThemes = themes.filter(t => t.phase === 'Implementation');
  const crossCuttingThemes = themes.filter(t => t.phase === 'Cross-Cutting');

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'complete':
        return <CheckCircle2 className="size-5 text-green-600" />;
      case 'in-progress':
        return <TrendingUp className="size-5 text-blue-600" />;
      default:
        return <Circle className="size-5 text-gray-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'complete':
        return 'Complete';
      case 'in-progress':
        return 'In Progress';
      default:
        return 'Not Started';
    }
  };

  return (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-blue-900 text-sm">Total Themes</div>
          <div className="text-blue-900 mt-1">{themes.length}</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="text-green-900 text-sm">Complete</div>
          <div className="text-green-900 mt-1">{themes.filter(t => t.status === 'complete').length}</div>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="text-purple-900 text-sm">Ready to Build</div>
          <div className="text-purple-900 mt-1">{buildReadiness?.summary.readyCount || 0} CIs</div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="text-yellow-900 text-sm">AI Insights</div>
          <div className="text-yellow-900 mt-1">{context.crossThemeInsights.length}</div>
        </div>
      </div>

      {/* Foundation Phase */}
      <div>
        <h3 className="text-gray-900 mb-3">Foundation Phase (Critical)</h3>
        <div className="space-y-2">
          {foundationThemes.map(theme => (
            <button
              key={theme.id}
              onClick={() => onSelectTheme(theme.id)}
              className="w-full flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all"
            >
              <div className="flex items-center gap-3">
                {getStatusIcon(theme.status)}
                <div className="text-left">
                  <div className="text-gray-900">{theme.name}</div>
                  <div className="text-sm text-gray-600">{getStatusText(theme.status)}</div>
                </div>
              </div>
              <ChevronRight className="size-5 text-gray-400" />
            </button>
          ))}
        </div>
      </div>

      {/* Implementation Phase */}
      <div>
        <h3 className="text-gray-900 mb-3">Implementation Phase</h3>
        <div className="space-y-2">
          {implementationThemes.map(theme => (
            <button
              key={theme.id}
              onClick={() => onSelectTheme(theme.id)}
              className="w-full flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all"
            >
              <div className="flex items-center gap-3">
                {getStatusIcon(theme.status)}
                <div className="text-left">
                  <div className="text-gray-900">{theme.name}</div>
                  <div className="text-sm text-gray-600">
                    {getStatusText(theme.status)}
                    {theme.configItems && Object.keys(theme.configItems).length > 0 && (
                      <span className="ml-2">• {Object.keys(theme.configItems).length} CIs</span>
                    )}
                  </div>
                </div>
              </div>
              <ChevronRight className="size-5 text-gray-400" />
            </button>
          ))}
        </div>
      </div>

      {/* Cross-Cutting */}
      <div>
        <h3 className="text-gray-900 mb-3">Cross-Cutting</h3>
        <div className="space-y-2">
          {crossCuttingThemes.map(theme => (
            <button
              key={theme.id}
              onClick={() => onSelectTheme(theme.id)}
              className="w-full flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all"
            >
              <div className="flex items-center gap-3">
                {getStatusIcon(theme.status)}
                <div className="text-left">
                  <div className="text-gray-900">{theme.name}</div>
                  <div className="text-sm text-gray-600">{getStatusText(theme.status)}</div>
                </div>
              </div>
              <ChevronRight className="size-5 text-gray-400" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Themes View Component
function ThemesView({ 
  context, 
  selectedTheme,
  onSelectTheme,
  companyId,
  accessToken
}: { 
  context: DiscoveryContext;
  selectedTheme: string | null;
  onSelectTheme: (themeId: string) => void;
  companyId: string;
  accessToken: string;
}) {
  // If a theme is selected, show detail view
  if (selectedTheme) {
    return (
      <ThemeDetailView
        themeId={selectedTheme}
        companyId={companyId}
        accessToken={accessToken}
        onBack={() => onSelectTheme(null as any)}
      />
    );
  }

  // Otherwise show theme selector
  const themes = Object.values(context.discoveryThemes);
  
  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-blue-900 mb-1">Select a Theme</h3>
        <p className="text-sm text-blue-700">
          Choose a discovery theme to see all CIs, domain questions, and cross-CI opportunities.
        </p>
      </div>

      <div className="space-y-2">
        {themes.map(theme => (
          <button
            key={theme.id}
            onClick={() => onSelectTheme(theme.id)}
            className="w-full flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all"
          >
            <div className="flex items-center gap-3">
              {theme.status === 'complete' ? (
                <CheckCircle2 className="size-5 text-green-600" />
              ) : theme.status === 'in-progress' ? (
                <TrendingUp className="size-5 text-blue-600" />
              ) : (
                <Circle className="size-5 text-gray-400" />
              )}
              <div className="text-left">
                <div className="text-gray-900">{theme.name}</div>
                <div className="text-sm text-gray-600">
                  {theme.phase} • {theme.status === 'complete' ? 'Complete' : theme.status === 'in-progress' ? 'In Progress' : 'Not Started'}
                  {theme.configItems && Object.keys(theme.configItems).length > 0 && (
                    <span className="ml-2">• {Object.keys(theme.configItems).length} CIs</span>
                  )}
                </div>
              </div>
            </div>
            <ChevronRight className="size-5 text-gray-400" />
          </button>
        ))}
      </div>
    </div>
  );
}

// Build Readiness View Component (placeholder)
function BuildReadinessView({ 
  buildReadiness,
  companyId,
  accessToken
}: { 
  buildReadiness: BuildReadinessSummary;
  companyId: string;
  accessToken: string;
}) {
  return (
    <div className="space-y-4">
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <h3 className="text-green-900 mb-2">Ready to Build ({buildReadiness.summary.readyCount})</h3>
        {buildReadiness.readyToBuild.length === 0 ? (
          <p className="text-green-700">No CIs ready yet. Continue answering discovery questions to progress.</p>
        ) : (
          <div className="space-y-2">
            {buildReadiness.readyToBuild.map(ci => (
              <div key={ci.id} className="bg-white border border-green-300 rounded p-3">
                <div className="text-gray-900">{ci.name}</div>
                <div className="text-sm text-gray-600">
                  {ci.themeName} • {ci.readiness.percentage}% complete
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-blue-900 mb-2">In Progress ({buildReadiness.summary.inProgressCount})</h3>
        {buildReadiness.inProgress.length === 0 ? (
          <p className="text-blue-700">No CIs in progress</p>
        ) : (
          <div className="text-sm text-blue-700">{buildReadiness.inProgress.length} CIs in discovery</div>
        )}
      </div>
    </div>
  );
}

// Insights View Component (placeholder)
function InsightsView({ 
  insights,
  onRunAnalysis,
  analyzing
}: { 
  insights: CrossThemeInsight[];
  onRunAnalysis: () => void;
  analyzing: boolean;
}) {
  if (insights.length === 0) {
    return (
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 text-center">
        <Lightbulb className="size-12 text-purple-600 mx-auto mb-3" />
        <h3 className="text-purple-900 mb-2">No AI Insights Yet</h3>
        <p className="text-purple-700 mb-4">
          Answer discovery questions across multiple themes, then run AI analysis to identify patterns and opportunities.
        </p>
        <button
          onClick={onRunAnalysis}
          disabled={analyzing}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
        >
          {analyzing ? 'Analyzing...' : 'Run Analysis Now'}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {insights.map(insight => (
        <div 
          key={insight.id}
          className={`border rounded-lg p-4 ${
            insight.confidence === 'high'
              ? 'bg-green-50 border-green-200'
              : insight.confidence === 'medium'
              ? 'bg-yellow-50 border-yellow-200'
              : 'bg-gray-50 border-gray-200'
          }`}
        >
          <div className="flex items-start justify-between mb-2">
            <div className="text-gray-900">{insight.insight}</div>
            {insight.actionable && (
              <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">
                Action Required
              </span>
            )}
          </div>
          <div className="text-sm text-gray-700 mb-2">
            <strong>Recommendation:</strong> {insight.recommendation}
          </div>
          <div className="text-xs text-gray-600">
            Affects: {insight.affectedThemes.join(', ')} • Confidence: {insight.confidence}
          </div>
        </div>
      ))}
    </div>
  );
}

// Question Templates View Component (placeholder)
function QuestionTemplatesView({ 
  companyId,
  accessToken
}: { 
  companyId: string;
  accessToken: string;
}) {
  return (
    <QuestionTemplateManager
      companyId={companyId}
      accessToken={accessToken}
    />
  );
}